import { doc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../utils/firebase';
import { useAuth } from './useAuth';

export function useUserProfile() {
  const { user, userProfile } = useAuth();

  // Update profile field
  const updateProfile = async (updates) => {
    if (!user?.uid) return;

    const userDocRef = doc(db, 'users', user.uid);
    await updateDoc(userDocRef, {
      ...updates,
      updatedAt: new Date()
    });
  };

  // Toggle task management on/off
  const toggleTaskManagement = async (enabled) => {
    await updateProfile({ taskManagementEnabled: enabled });
  };

  // Update display name
  const updateDisplayName = async (displayName) => {
    const firstName = displayName?.split(' ')[0] || '';
    await updateProfile({ displayName, firstName });
  };

  // Connect Slack - redirects to Slack OAuth
  const connectSlack = async () => {
    const getSlackAuthUrl = httpsCallable(functions, 'getSlackAuthUrl');
    const result = await getSlackAuthUrl();
    window.location.href = result.data.url;
  };

  // Disconnect Slack - removes Slack user ID from profile
  const disconnectSlack = async () => {
    await updateProfile({
      slackUserId: null,
      slackTeamId: null,
      slackConnectedAt: null
    });
  };

  // Connect Gmail - redirects to Gmail OAuth
  const connectGmail = async () => {
    const getGmailAuthUrl = httpsCallable(functions, 'getGmailAuthUrl');
    const result = await getGmailAuthUrl();
    window.location.href = result.data.url;
  };

  // Disconnect Gmail - removes Gmail connection from profile
  const disconnectGmail = async () => {
    const disconnect = httpsCallable(functions, 'disconnectGmail');
    await disconnect();
  };

  // Generate iOS Shortcut API key
  const generateShortcutApiKey = async () => {
    const generateUserApiKey = httpsCallable(functions, 'generateUserApiKey');
    const result = await generateUserApiKey();
    return result.data.apiKey;
  };

  return {
    userProfile,
    updateProfile,
    toggleTaskManagement,
    updateDisplayName,
    connectSlack,
    disconnectSlack,
    connectGmail,
    disconnectGmail,
    generateShortcutApiKey,
    // Convenience getters
    taskManagementEnabled: userProfile?.taskManagementEnabled ?? true,
    managerId: userProfile?.managerId,
    supervisorIds: userProfile?.supervisorIds || [],
    slackUserId: userProfile?.slackUserId || null,
    slackConnectedAt: userProfile?.slackConnectedAt || null,
    gmailConnected: userProfile?.gmailConnected || false,
    gmailEmail: userProfile?.gmailEmail || null,
    gmailConnectedAt: userProfile?.gmailConnectedAt || null,
    shortcutApiKey: userProfile?.shortcutApiKey || null
  };
}
